import type { CSSProperties, ReactNode } from 'react'
import { useT } from '@/i18n/useT'
import { Icon } from './Icon'

/** Pills, banners and tags — the design's status vocabulary. */

export function Pill({
  children,
  fg = 'var(--muted)',
  bg = 'transparent',
  border = 'var(--line)',
  style,
}: {
  children: ReactNode
  fg?: string
  bg?: string
  border?: string
  style?: CSSProperties
}) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        color: fg,
        background: bg,
        whiteSpace: 'nowrap',
        // Must be explicit: the DataGrid sets line-height to the row height to
        // centre plain text, and an inherited 51px would balloon the pill into
        // an ellipse. Same reason flexShrink is pinned — a shrunk box with a
        // 999px radius stops looking like a pill.
        lineHeight: 1.5,
        flexShrink: 0,
        width: 'fit-content',
        ...style,
      }}
    >
      {children}
    </span>
  )
}

/** The monospace file-type badge (PDF / XLSX / MAIL). */
export function FileTag({
  label,
  fg,
  bg,
  size = 10,
}: {
  label: string
  fg: string
  bg: string
  size?: number
}) {
  return (
    <span
      style={{
        fontFamily: 'var(--mono)',
        fontSize: size,
        fontWeight: 700,
        letterSpacing: '0.06em',
        padding: '4px 6px',
        borderRadius: 4,
        color: fg,
        background: bg,
        // Explicit for the same reason as Pill: never inherit a table's
        // row-height line-height.
        lineHeight: 1.5,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  )
}

export function Dot({ color, size = 9 }: { color: string; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
  )
}

/** Green confirmation strip with a dismiss cross. */
export function Notice({ children, onClose }: { children: ReactNode; onClose?: () => void }) {
  const { t } = useT()
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        border: '1px solid var(--ok)',
        background: 'var(--okSoft)',
        color: 'var(--text)',
        borderRadius: 10,
        padding: '12px 16px',
        fontSize: 13,
      }}
    >
      <Icon name="check" size={16} strokeWidth={2.4} style={{ color: 'var(--ok)' }} />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button
          type="button"
          aria-label={t('Close')}
          onClick={onClose}
          style={{
            cursor: 'pointer',
            color: 'var(--muted)',
            fontSize: 16,
            lineHeight: 1,
            background: 'none',
            border: 'none',
            padding: 0,
          }}
        >
          ✕
        </button>
      )}
    </div>
  )
}

export function Banner({
  tone,
  children,
  actions,
  title,
}: {
  tone: 'warn' | 'err' | 'ok'
  children?: ReactNode
  actions?: ReactNode
  title?: ReactNode
}) {
  const border = tone === 'warn' ? 'var(--warn)' : tone === 'err' ? 'var(--err)' : 'var(--ok)'
  const bg = tone === 'warn' ? 'var(--warnSoft)' : tone === 'err' ? 'var(--errSoft)' : 'var(--okSoft)'
  return (
    <div
      style={{
        border: `1px solid ${border}`,
        background: bg,
        borderRadius: 10,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <Icon
        name={tone === 'ok' ? 'check' : 'warning'}
        size={18}
        strokeWidth={2.2}
        style={{ color: border, marginTop: 2 }}
      />
      <div style={{ flex: 1, minWidth: 240 }}>
        {title != null && <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>}
        {children != null && (
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: title != null ? 4 : 0 }}>
            {children}
          </div>
        )}
      </div>
      {actions != null && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
    </div>
  )
}

/** Warning list inside a bordered block — the drawer's review notes. */
export function WarningBlock({ items, label }: { items: string[]; label?: string }) {
  const { t } = useT()
  if (items.length === 0) return null
  return (
    <div
      style={{
        border: '1px solid var(--warn)',
        background: 'var(--warnSoft)',
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--warn)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        <Icon name="warning" size={15} strokeWidth={2.2} />
        {label ?? t('Review notes')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {items.map((w, i) => (
          <div key={i} style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.45 }}>
            {w}
          </div>
        ))}
      </div>
    </div>
  )
}

/** Collapsible amber strip that fronts a list of warnings. */
export function WarningAccordion({
  items,
  open,
  onToggle,
}: {
  items: string[]
  open: boolean
  onToggle: () => void
}) {
  const { t } = useT()
  if (items.length === 0) return null
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
      <div
        onClick={onToggle}
        style={{
          padding: '11px 14px',
          background: 'var(--warnSoft)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
        }}
      >
        <Icon name="warning" size={15} strokeWidth={2.2} style={{ color: 'var(--warn)' }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--warn)', flex: 1 }}>
          {items.length} {items.length === 1 ? t('review note') : t('review notes')}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('show / hide')}</div>
      </div>
      {open && (
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((w, i) => (
            <div key={i} style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
              {w}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Label/value pairs on a --field tile, as used in both drawers. */
export function StatTile({
  label,
  value,
  fg = 'var(--text)',
  valueSize = 17,
}: {
  label: ReactNode
  value: ReactNode
  fg?: string
  valueSize?: number
}) {
  return (
    <div
      style={{
        background: 'var(--field)',
        border: '1px solid var(--line2)',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--faint)' }}>{label}</div>
      <div style={{ fontSize: valueSize, fontWeight: 700, marginTop: 4, color: fg }}>{value}</div>
    </div>
  )
}
